// frontend/src/components/PotPage.jsx
import React, { useEffect, useState } from "react";
import "./PotPage.css";

/**
 * Weekly Pot logic (13 weeks total):
 *  - base = (number of players) * $10
 *  - Week 1 pot = base
 *  - If no winner in a week, NEXT week pot = base * (streak + 1)
 *  - If winner in a week, NEXT week streak resets to 0 (back to base)
 *
 * This component:
 *  - Computes pots only up to currentWeek (from backend current_week.json)
 *  - Future weeks show “—”
 */

const TOTAL_WEEKS = 13;

export default function PotPage() {
  const [playerCount, setPlayerCount] = useState(0);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [potData, setPotData] = useState({});
  const [loading, setLoading] = useState(true);

  const backendBase =
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    (window.location.hostname === "localhost"
      ? "http://localhost:5001"
      : "https://pickem-backend-2025.onrender.com");

  // ---------------- helpers ----------------
  async function getJSON(url) {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(String(res.status));
    return res.json();
  }

  function coercePlayerCount(data) {
    if (Array.isArray(data)) return data.length;
    if (data && Array.isArray(data.players)) return data.players.length;
    if (data && Array.isArray(data.roster)) return data.roster.length;
    return 0;
  }

  // Try multiple filenames / shapes for winners
  async function getWinnersForWeek(week) {
    const candidates = [
      `${backendBase}/data/winners_week_${week}.json`,
      `${backendBase}/data/totalwinners_week_${week}.json`,
      `${backendBase}/data/winners_detail_week_${week}.json`,
    ];

    for (const url of candidates) {
      try {
        const data = await getJSON(url);

        if (Array.isArray(data)) {
          const hasWinner = data.some(
            (x) =>
              (x?.total ??
                x?.weeklyTotal ??
                x?.weekly_points ??
                x?.points ??
                x?.correct ??
                x?.correctPicks ??
                0) >= 9
          );

          return { known: true, hasWinner };
        }

        if (data && Array.isArray(data.winners) && data.winners.length > 0) {
          return { known: true, hasWinner: true };
        }

        if (data && typeof data.totalWinners === "number") {
          return { known: true, hasWinner: data.totalWinners > 0 };
        }

        return { known: false, hasWinner: false };
      } catch {
        // try next candidate
      }
    }

    return { known: false, hasWinner: false };
  }

  // 1) Load roster count
  useEffect(() => {
    (async () => {
      try {
        const data = await getJSON(`${backendBase}/data/roster.json`);
        setPlayerCount(coercePlayerCount(data));
      } catch {
        setPlayerCount(0);
      }
    })();
  }, [backendBase]);

  // 2) Load current week
  useEffect(() => {
    (async () => {
      try {
        const wk = await getJSON(`${backendBase}/data/current_week.json`);

        const val =
          typeof wk === "number"
            ? wk
            : wk?.currentWeek ?? wk?.week ?? wk?.current_week ?? 1;

        setCurrentWeek(val || 1);
      } catch {
        setCurrentWeek(1);
      }
    })();
  }, [backendBase]);

  // 3) Compute pots only through currentWeek
  useEffect(() => {
    if (!playerCount || !currentWeek) {
      setPotData({});
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);

      const base = playerCount * 10;
      const tracker = {};
      let noWinStreak = 0;

      for (let w = 1; w <= currentWeek; w++) {
        const potForThisWeek = base * (noWinStreak + 1);
        tracker[w] = potForThisWeek;

        const { known, hasWinner } = await getWinnersForWeek(w);

        if (!known) {
          // No completed winner data yet.
          // Do not alter the rollover streak.
          continue;
        }

        if (hasWinner) {
          noWinStreak = 0;
        } else {
          noWinStreak += 1;
        }
      }

      setPotData(tracker);
      setLoading(false);
    })();
  }, [playerCount, currentWeek, backendBase]);

  // ---------------- render ----------------
  return (
    <div className="page-container">
      <h2>🏆 Weekly Pot Totals</h2>

      {loading && <p>Loading…</p>}

      {!loading &&
        [...Array(TOTAL_WEEKS)].map((_, i) => {
          const week = i + 1;
          const amount = potData[week];

          return (
            <div key={week} className="pot-box">
              Week {week}: {amount !== undefined ? `$${amount}` : "—"}
            </div>
          );
        })}

      {!loading && playerCount === 0 && (
        <p style={{ color: "#b00020" }}>
          Couldn’t read <code>roster.json</code> — player count is 0. Check your
          backend <code>/data/roster.json</code>.
        </p>
      )}
    </div>
  );
}